import React from 'react';
import { Tag } from 'antd';
import { HtmlContent } from '@client/utils/html_content';
import ItemsWithPopover from '@client/components/ui/table/ItemWithPopover';
import RiskSymbolDisplay from '@client/components/ui/RiskSymbolDisplay';
import TruncatedText from '@client/components/ui/TruncatedText';
import { ProductTypeLegend } from '@client/components/ui/productLegends';

/**
 * Cell component for displaying CAS (Chemical Abstracts Service) numbers
 *
 * Renders a list of CAS numbers with popover functionality for long lists.
 * Automatically handles overflow and provides clean display.
 *
 * @param {Object} props - Component props
 * @param {string[]} props.casNumbers - Array of CAS number strings
 * @returns {JSX.Element} Formatted CAS numbers cell
 *
 * @example
 * <CasNumbersCell casNumbers={['123-45-6', '789-01-2']} />
 */
export const CasNumbersCell = ({ casNumbers }) => (
  <ItemsWithPopover items={casNumbers} getItemName={(cas) => cas} />
);

/**
 * Cell component for displaying category lists with name properties
 *
 * Generic component for any category-type data that has a 'name' property.
 * Handles multiple categories with consistent formatting.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.categories - Array of category objects
 * @param {string} props.categories[].name - Category name
 * @returns {JSX.Element} Formatted categories cell
 *
 * @example
 * <CategoriesCell categories={[{name: 'Herbicide'}, {name: 'Fungicide'}]} />
 */
export const CategoriesCell = ({ categories, label = 'name' }) => (
  <ItemsWithPopover items={categories} getItemName={(cat) => cat?.[label]} />
);

/**
 * Cell component for displaying pesticide groups from association data
 *
 * Extracts and displays pesticide group names from complex association objects.
 * Handles nested data structure safely with null checking.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of pesticide group associations
 * @param {Object} props.associations[].pesticide_group - Pesticide group object
 * @param {string} props.associations[].pesticide_group.name - Group name
 * @returns {JSX.Element} Formatted pesticide groups cell
 *
 * @example
 * <PesticideGroupsCell associations={[{pesticide_group: {name: 'Group A'}}]} />
 */
export const PesticideGroupsCell = ({ associations }) => (
  <ItemsWithPopover
    items={associations}
    getItemName={(association) => association.pesticide_group?.name}
    isNested={true}
  />
);

/**
 * Cell component for displaying chemical families from association data
 *
 * Filters associations to only show those with chemical family data,
 * then displays the family labels in a clean format.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of chemical family associations
 * @param {Object} props.associations[].chemical_family - Chemical family object
 * @param {string} props.associations[].chemical_family.label - Family label
 * @returns {JSX.Element} Formatted chemical families cell
 *
 * @example
 * <ChemicalFamiliesCell associations={[{chemical_family: {label: 'Organochlorine'}}]} />
 */
export const ChemicalFamiliesCell = ({ associations }) => {
  const familiesWithData =
    associations?.filter((assoc) => assoc.chemical_family) || [];

  return (
    <ItemsWithPopover
      items={familiesWithData}
      getItemName={(association) => association.chemical_family?.label}
    />
  );
};

/**
 * Cell component for displaying resistance development risk information
 *
 * Extracts resistance risk data from nested association structures.
 * Only displays associations that have resistance development data.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of pesticide associations
 * @param {Object} props.associations[].pesticide_group - Pesticide group object
 * @param {Object} props.associations[].pesticide_group.risk_of_resistance_development - Risk object
 * @param {string} props.associations[].pesticide_group.risk_of_resistance_development.name - Risk name
 * @returns {JSX.Element} Formatted resistance risk cell
 *
 * @example
 * <ResistanceRiskCell associations={[{pesticide_group: {risk_of_resistance_development: {name: 'High'}}}]} />
 */
export const ResistanceRiskCell = ({ associations }) => {
  const risksWithData =
    associations?.filter(
      (assoc) => assoc.pesticide_group?.risk_of_resistance_development
    ) || [];

  return (
    <ItemsWithPopover
      items={risksWithData}
      getItemName={(association) =>
        association.pesticide_group?.risk_of_resistance_development?.name
      }
    />
  );
};

/**
 * Cell component for displaying risk symbols
 *
 * Uses the dedicated RiskSymbolDisplay component to show risk symbols
 * with perfect alignment and consistent styling across the application.
 *
 * @param {Object} props - Component props
 * @param {Object|number} props.symbolData - Symbol data object or legacy integer value
 * @param {string} [props.iconSrc] - Fallback icon source URL (for legacy support)
 * @param {string} [props.alt='Risk symbol'] - Alt text for accessibility
 * @returns {JSX.Element} Risk symbol display
 *
 * @example
 * <RiskSymbolCell symbolData={{ calculated_value: 3, symbol: { image: "/media/symbol_image/3.png", label: "faible" } }} />
 */
export const RiskSymbolCell = ({
  symbolData,
  iconSrc,
  alt = 'Risk symbol',
}) => {
  return (
    <RiskSymbolDisplay
      symbolData={symbolData}
      size="sm"
      fallbackIcon={iconSrc}
      alt={alt}
      hasBackground={false}
    />
  );
};

/**
 * Cell component for rendering HTML content safely
 *
 * Wrapper around HtmlContent utility for consistent HTML rendering in tables.
 * Handles HTML sanitization and formatting automatically.
 *
 * @param {Object} props - Component props
 * @param {string} props.html - HTML string to render
 * @returns {JSX.Element} Safely rendered HTML content
 *
 * @example
 * <HtmlContentCell html="<strong>Substance Name</strong>" />
 */
export const HtmlContentCell = ({ html }) => <HtmlContent html={html} />;

/**
 * Cell component for rendering action links
 *
 * Generic component for creating consistent action links in table cells.
 * Supports customizable href, target, and styling.
 *
 * @param {Object} props - Component props
 * @param {string} props.href - Link destination URL
 * @param {string} [props.target='_blank'] - Link target behavior
 * @param {string} props.className - CSS classes for styling
 * @param {React.ReactNode} props.children - Link content/text
 * @returns {JSX.Element} Formatted action link
 *
 * @example
 * <ActionLinkCell
 *   href="/details/123"
 *   className="btn btn-primary"
 * >
 *   View Details
 * </ActionLinkCell>
 */
export const ActionLinkCell = ({
  href,
  target = '_blank',
  className,
  children,
}) => (
  <a href={href} target={target} className={className} rel="noreferrer">
    {children}
  </a>
);

/**
 * Cell component for displaying collections of items as tags
 *
 * Renders arrays of data as visually organized tag collections.
 * Supports custom key generation and name extraction functions.
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display as tags
 * @param {Function} props.getItemName - Function to extract display name from item
 * @param {Function} [props.getItemKey] - Function to generate unique keys
 * @returns {JSX.Element} Collection of formatted tags
 *
 * @example
 * <TagsCell
 *   items={sectors}
 *   getItemName={(sector) => sector.name}
 *   getItemKey={(sector) => sector.id}
 * />
 */
export const TagsCell = ({ items, getItemName, getItemKey }) => (
  <div className="flex flex-wrap gap-2">
    {items?.map((item, index) => (
      <Tag
        key={getItemKey ? getItemKey(item, index) : `tag-${index}`}
        size="small"
      >
        {getItemName(item)}
      </Tag>
    ))}
  </div>
);

export const TreatmentProductsCell = ({ TreatmentProducts }) => {
  const getAllActiveSubstances = () => {
    const activeSubstancesMap = new Map();

    TreatmentProducts?.forEach((treatmentProduct) => {
      treatmentProduct?.product?.active_substances?.forEach(
        (activeSubstance) => {
          const asId = activeSubstance?.active_substance?.id;
          if (asId && !activeSubstancesMap.has(asId)) {
            activeSubstancesMap.set(asId, activeSubstance);
          }
        }
      );
    });

    return Array.from(activeSubstancesMap.values());
  };

  const allActiveSubstances = getAllActiveSubstances();

  return (
    <ItemsWithPopover
      items={TreatmentProducts}
      getItemName={(treatmentProduct) => {
        return treatmentProduct?.product?.name;
      }}
      customPopoverContent={({ items }) => (
        <div className="flex flex-col gap-1 max-w-sm">
          {items.map((item, index) => (
            <Tag
              key={index}
              size="small"
              className="whitespace-normal break-words"
            >
              <HtmlContent html={item?.product?.name} />
            </Tag>
          ))}
        </div>
      )}
      CustomVisibleContent={({ items }) => (
        <>
          {items.map((item, index) => (
            <div className="flex flex-col gap-2 justify-start" key={index}>
              <div className="flex items-center justify-center gap-[10px] self-start px-3 py-[2px] rounded-lg bg-[#F5F5F5] text-[#105235] font-roboto text-[14px] font-bold leading-[22px] underline">
                <TruncatedText text={item?.product?.name} maxLength={30} />
                {index < items.length - 1 && ', '}
              </div>
              <div>
                {item?.selection_type === 'series' && (
                  <ProductTypeLegend type="serie" />
                )}
              </div>
              <div>
                {item?.product?.is_organic_approval && (
                  <ProductTypeLegend type="biological" height={22} />
                )}
              </div>
              {allActiveSubstances.length > 0 && (
                <>
                  <ItemsWithPopover
                    items={allActiveSubstances}
                    getItemName={(activeSubstance) => {
                      const pesticidGroups =
                        activeSubstance?.active_substance?.pesticide_group_associations
                          ?.map((assoc) => assoc.pesticide_group?.name)
                          .filter((name) => name)
                          .join(', ');
                      return `${activeSubstance?.active_substance?.name} ( ${pesticidGroups} )`;
                    }}
                    isNested={true}
                    CustomVisibleContent={({ items }) => (
                      <div className="flex flex-wrap gap-2">
                        {items.map((activeSubstance, idx) => {
                          const pesticidGroups =
                            activeSubstance?.active_substance?.pesticide_group_associations
                              ?.map((assoc) => assoc.pesticide_group?.name)
                              .filter((name) => name)
                              .join(', ');
                          const displayText = `${activeSubstance?.active_substance?.name}${pesticidGroups ? ` (${pesticidGroups})` : ''}`;

                          return <HtmlContent key={idx} html={displayText} />;
                        })}
                      </div>
                    )}
                  />
                </>
              )}
            </div>
          ))}
        </>
      )}
    />
  );
};

export const SimpleTextCell = ({ text }) =>
  text ? (
    <TruncatedText text={text} maxLength={100} />
  ) : (
    <span className="text-gray-400 text-xs italic font-light">N/A</span>
  );

export const ActivityTypesCell = ({ treatmentProducts }) => {
  if (!treatmentProducts?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const activityTypesMap = new Map();

  treatmentProducts.forEach((treatmentProduct) => {
    treatmentProduct?.product?.active_substances?.forEach(
      (activeSubstanceProduct) => {
        activeSubstanceProduct?.active_substance?.activity_types?.forEach(
          (activityType) => {
            if (
              activityType?.short_name &&
              !activityTypesMap.has(activityType.id)
            ) {
              activityTypesMap.set(activityType.id, {
                id: activityType.id,
                short_name: activityType.short_name,
                label: activityType.label,
                description: activityType.description,
              });
            }
          }
        );
      }
    );
  });

  const activityTypes = Array.from(activityTypesMap.values());

  if (activityTypes.length === 0) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <ItemsWithPopover
      items={activityTypes}
      getItemName={(activityType) => activityType.short_name}
      maxVisible={1}
      isNested={true}
      CustomPopoverContent={({ items }) => (
        <div className="flex flex-col max-w-lg">
          {items.map((activityType, index) => (
            <div
              key={index}
              className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
            >
              <div className="font-medium text-sm text-gray-900 leading-relaxed mb-1">
                {activityType.short_name}
              </div>
              {activityType.description && (
                <div className="text-xs text-gray-500 leading-relaxed">
                  {activityType.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    />
  );
};
